// ============================================================
// pages/admin/AdminSettings.jsx — Business Settings (Firebase)
// ============================================================

import React, { useState, useEffect } from "react";
import { BusinessSettingsService } from "../../services/firebaseServices";

const DAY_NAMES = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const DEFAULT_SETTINGS = {
  businessName: "Chaitimecrunch",
  tagline: "Artisan Bakery · Beau Bassin",
  phone: "+230 5710 0994",
  email: "hello@chaitimecrunch.mu",
  address: "Beau Bassin, Plaines Wilhems, Mauritius",
  location: { latitude: -20.2333, longitude: 57.4667 },
  socialLinks: { whatsapp: "", instagram: "", facebook: "", tiktok: "" },
  workingHours: Object.fromEntries(
    DAY_NAMES.map((d) => [d, { isOpen: !["sunday"].includes(d), open: "08:00", close: d === "saturday" ? "16:00" : "18:00" }])
  ),
  maxBookingsPerSlot: 5,
  deliveryFee: 100,
  deliveryRadius: "Plaines Wilhems district",
  blockedDates: [],
  announcementBanner: "",
  aboutTitle: "Our Story",
  aboutText: "",
};

export default function AdminSettings({ showToast }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState("");

  useEffect(() => {
    BusinessSettingsService.get()
      .then((data) => setSettings(data || { ...DEFAULT_SETTINGS }))
      .catch(() => setSettings({ ...DEFAULT_SETTINGS }))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => setSettings((prev) => ({ ...prev, [key]: val }));
  const updateNested = (parent, key, val) =>
    setSettings((prev) => ({ ...prev, [parent]: { ...prev[parent], [key]: val } }));

  const updateHours = (day, field, val) => {
    setSettings((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: { ...prev.workingHours[day], [field]: field === "isOpen" ? val : val },
      },
    }));
  };

  const addBlockedDate = () => {
    if (!newBlockedDate) return;
    const current = settings.blockedDates || [];
    if (current.includes(newBlockedDate)) return;
    update("blockedDates", [...current, newBlockedDate].sort());
    setNewBlockedDate("");
  };

  const removeBlockedDate = (date) => {
    update("blockedDates", (settings.blockedDates || []).filter((d) => d !== date));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await BusinessSettingsService.update(settings);
      showToast("Settings saved!", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) return <div className="empty-state"><p>Loading settings...</p></div>;

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Business Settings</h2>
          <p>Manage your bakery's info, hours, and booking configuration</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "💾 Save All Changes"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
        {/* Business Info */}
        <div className="card">
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Business Info</h3>

          <div className="form-group">
            <label className="form-label">Business Name</label>
            <input className="form-input" value={settings.businessName}
              onChange={(e) => update("businessName", e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Tagline</label>
            <input className="form-input" value={settings.tagline}
              onChange={(e) => update("tagline", e.target.value)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={settings.phone}
                onChange={(e) => update("phone", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={settings.email}
                onChange={(e) => update("email", e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" value={settings.address}
              onChange={(e) => update("address", e.target.value)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div className="form-group">
              <label className="form-label">Latitude</label>
              <input className="form-input" type="number" step="0.0001" value={settings.location?.latitude || ""}
                onChange={(e) => updateNested("location", "latitude", parseFloat(e.target.value))} />
            </div>
            <div className="form-group">
              <label className="form-label">Longitude</label>
              <input className="form-input" type="number" step="0.0001" value={settings.location?.longitude || ""}
                onChange={(e) => updateNested("location", "longitude", parseFloat(e.target.value))} />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div>
          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Social Links</h3>
            {["whatsapp", "instagram", "facebook", "tiktok"].map((platform) => (
              <div className="form-group" key={platform}>
                <label className="form-label" style={{ textTransform: "capitalize" }}>{platform}</label>
                <input className="form-input" value={settings.socialLinks?.[platform] || ""}
                  onChange={(e) => updateNested("socialLinks", platform, e.target.value)}
                  placeholder={`https://${platform}.com/...`} />
              </div>
            ))}
          </div>

          {/* Booking Config */}
          <div className="card">
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Booking Configuration</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div className="form-group">
                <label className="form-label">Max Bookings / Slot</label>
                <input className="form-input" type="number" value={settings.maxBookingsPerSlot}
                  onChange={(e) => update("maxBookingsPerSlot", Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Fee (Rs)</label>
                <input className="form-input" type="number" value={settings.deliveryFee}
                  onChange={(e) => update("deliveryFee", Number(e.target.value))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Delivery Area</label>
              <input className="form-input" value={settings.deliveryRadius || ""}
                onChange={(e) => update("deliveryRadius", e.target.value)}
                placeholder="e.g. Plaines Wilhems district" />
            </div>
          </div>
        </div>
      </div>

      {/* Working Hours */}
      <div className="card" style={{ marginTop: "1.5rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Working Hours</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {DAY_NAMES.map((day) => {
            const h = settings.workingHours?.[day] || { isOpen: false, open: "08:00", close: "18:00" };
            return (
              <div key={day} style={{
                display: "flex", alignItems: "center", gap: "1rem",
                padding: "0.6rem 0.75rem", borderRadius: 8,
                background: h.isOpen ? "var(--cream)" : "rgba(231,76,60,0.04)",
              }}>
                <label style={{
                  display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
                  minWidth: 140, fontWeight: 600, fontSize: "0.9rem", textTransform: "capitalize",
                }}>
                  <input type="checkbox" checked={h.isOpen}
                    onChange={(e) => updateHours(day, "isOpen", e.target.checked)}
                    style={{ width: 18, height: 18 }} />
                  {day}
                </label>
                {h.isOpen ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input type="time" className="form-input" style={{ width: 130 }}
                      value={h.open} onChange={(e) => updateHours(day, "open", e.target.value)} />
                    <span style={{ color: "var(--cocoa)" }}>to</span>
                    <input type="time" className="form-input" style={{ width: 130 }}
                      value={h.close} onChange={(e) => updateHours(day, "close", e.target.value)} />
                  </div>
                ) : (
                  <span style={{ fontSize: "0.85rem", color: "#e74c3c" }}>Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Blocked Dates */}
      <div className="card" style={{ marginTop: "1.5rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Blocked Dates</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--cocoa)", marginBottom: "1rem" }}>
          Add dates when the bakery is closed (holidays, maintenance, etc.)
        </p>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <input type="date" className="form-input" style={{ flex: "0 0 200px" }}
            value={newBlockedDate} onChange={(e) => setNewBlockedDate(e.target.value)} />
          <button className="btn btn-outline btn-sm" onClick={addBlockedDate}>+ Add Date</button>
        </div>
        {(settings.blockedDates || []).length > 0 ? (
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {settings.blockedDates.map((date) => (
              <div key={date} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "0.3rem 0.75rem", borderRadius: 8,
                background: "rgba(231,76,60,0.08)", fontSize: "0.85rem",
              }}>
                {new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                <button onClick={() => removeBlockedDate(date)} style={{
                  background: "none", border: "none", color: "#e74c3c", cursor: "pointer",
                  fontWeight: 700, fontSize: "1rem",
                }}>✕</button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: "0.85rem", color: "var(--cocoa)" }}>No blocked dates set.</div>
        )}
      </div>

      {/* About Page Content */}
      <div className="card" style={{ marginTop: "1.5rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>About Page Content</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--cocoa)", marginBottom: "0.75rem" }}>
          Customise the "Our Story" section shown on the About page.
        </p>
        <div className="form-group">
          <label className="form-label">Section Title</label>
          <input className="form-input" value={settings.aboutTitle || ""}
            onChange={(e) => update("aboutTitle", e.target.value)}
            placeholder="Our Story" />
        </div>
        <div className="form-group">
          <label className="form-label">About Text</label>
          <textarea className="form-textarea" rows="6" value={settings.aboutText || ""}
            onChange={(e) => update("aboutText", e.target.value)}
            placeholder="Tell your bakery's story..." />
        </div>
      </div>

      {/* Announcement Banner */}
      <div className="card" style={{ marginTop: "1.5rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Announcement Banner</h3>
        <p style={{ fontSize: "0.85rem", color: "var(--cocoa)", marginBottom: "0.75rem" }}>
          Displayed at the top of the website. Leave empty to hide.
        </p>
        <textarea className="form-textarea" rows="2" value={settings.announcementBanner || ""}
          onChange={(e) => update("announcementBanner", e.target.value)}
          placeholder='e.g. "Free delivery on orders over Rs 500 this weekend!"' />
      </div>

      <div style={{ marginTop: "2rem", textAlign: "right" }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "💾 Save All Changes"}
        </button>
      </div>
    </div>
  );
}